// Copyright (c) Microsoft Corporation.
// Licensed under the MIT license.
import React, { useState, useMemo, useCallback } from 'react';
import { useConfig } from '../../contexts/ConfigContext';
import '../../pages/RecruiterDashboard.scss';
import { IconButton, Menu, MenuItem, ListItemIcon, ListItemText } from '@mui/material';
import LogoutOutlinedIcon from '@mui/icons-material/Logout';
import { useNavigate } from 'react-router-dom';

type SidebarProps = {
  activeNav?: string;
  user?: { name?: string; username?: string };
  onLogout?: () => void;
};

export default function Sidebar({ user, onLogout }: SidebarProps): JSX.Element {
  const navigate = useNavigate();
  const { config, logo } = useConfig();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  // Memoize user initial calculation to avoid unnecessary recalculations
  const userInitial = useMemo(() => {
    if (user?.name?.trim()) {
      return user.name.trim().charAt(0).toUpperCase();
    }
    if (user?.username?.trim()) {
      return user.username.trim().charAt(0).toUpperCase();
    }
    return 'U';
  }, [user?.name, user?.username]);

  // Memoize event handlers to prevent unnecessary re-renders
  const handleMenuOpen = useCallback((event: React.MouseEvent<HTMLElement>): void => {
    setAnchorEl(event.currentTarget);
  }, []);

  const handleMenuClose = useCallback((): void => {
    setAnchorEl(null);
  }, []);

  const handleLogout = useCallback((): void => {
    onLogout?.();
    setAnchorEl(null);
  }, [onLogout]);

  const handleLogoClick = useCallback((): void => {
    navigate('/');
  }, [navigate]);

  return (
    <aside className="sidebar">
      <IconButton onClick={handleLogoClick} className="sidebar-logo" aria-label="Go to home page">
        <img src={logo as string} alt={`${config?.companyName || 'G42'} Logo`} width={40} height={40} />
      </IconButton>

      <div className="sidebar-profile">
        <IconButton
          onClick={handleMenuOpen}
          className="sidebar-profile-btn"
          aria-controls={open ? 'sidebar-user-menu' : undefined}
          aria-haspopup="true"
          aria-expanded={open ? 'true' : undefined}
        >
          <div className="avatar avatar-initial" style={{ fontFamily: 'G42 Sans' }}>
            {userInitial}
          </div>
        </IconButton>

        <Menu
          anchorEl={anchorEl}
          open={open}
          onClose={handleMenuClose}
          onClick={handleMenuClose}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        >
          <MenuItem onClick={handleLogout}>
            <ListItemIcon>
              <LogoutOutlinedIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Logout</ListItemText>
          </MenuItem>
        </Menu>
      </div>
    </aside>
  );
}
